import {
  CalendarDays,
  Clock,
  Database,
  Pencil,
  Power,
  PowerOff,
  Webhook,
} from "lucide-react";

import { DataConnector, DataConnectorType } from "@/api/data-connectors";
import { Badge } from "@/design-system/components/Badge/Badge";
import { Button } from "@/design-system/components/Button/Button";

const TYPE_ICON: Record<DataConnectorType, typeof Webhook> = {
  api: Webhook,
  airbyte: Database,
};

const TYPE_LABEL: Record<DataConnectorType, string> = {
  api: "API",
  airbyte: "Airbyte",
};

const TYPE_DESCRIPTION: Record<DataConnectorType, string> = {
  api: "Push data into Freshpaint from your systems using the Freshpaint API.",
  airbyte:
    "Let Freshpaint pull data from your systems through an Airbyte connection.",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type Props = {
  dataConnector: DataConnector;
  onEdit: () => void;
  onToggleStatus: () => void;
  statusIsLoading?: boolean;
};

export function DataConnectorDetailHeader({
  dataConnector,
  onEdit,
  onToggleStatus,
  statusIsLoading,
}: Props) {
  const Icon = TYPE_ICON[dataConnector.type];
  const isDisabled = dataConnector.status === "disabled";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex min-w-0 gap-6">
        <div className="flex w-20 shrink-0 items-center justify-center self-stretch rounded-xl bg-blue-50 text-blue-500">
          <Icon className="h-9 w-9" strokeWidth={1.75} aria-hidden />
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-wrap items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-1">
              <h1 className="min-w-0 truncate text-2xl leading-8 font-semibold text-gray-900">
                {dataConnector.name || (
                  <span className="text-gray-400 italic">Untitled</span>
                )}
              </h1>
              <span className="text-textSubtle shrink-0 text-sm leading-5">
                {TYPE_LABEL[dataConnector.type]}
              </span>
              <Badge
                variant={isDisabled ? "unknown" : "success"}
                text={isDisabled ? "Disabled" : "Active"}
                className="min-w-0 px-2 py-0 text-xs"
              />
            </div>
            <p className="text-textSubtle min-w-0 text-sm leading-5">
              {TYPE_DESCRIPTION[dataConnector.type]}
            </p>

            <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 pt-1">
              <MetaItem
                icon={
                  <CalendarDays
                    className="h-4 w-4 shrink-0 text-gray-600"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                }
              >
                Created {formatDate(dataConnector.createdAt)}
              </MetaItem>
              <MetaItem
                icon={
                  <Clock
                    className="h-4 w-4 shrink-0 text-gray-600"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                }
              >
                Updated {formatDate(dataConnector.updatedAt)}
              </MetaItem>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" leadingIcon={<Pencil />} onClick={onEdit}>
              Edit
            </Button>
            <Button
              variant="outline"
              leadingIcon={isDisabled ? <Power /> : <PowerOff />}
              onClick={onToggleStatus}
              disabled={statusIsLoading}
            >
              {isDisabled ? "Enable" : "Disable"}
            </Button>
          </div>
        </div>
      </div>

      <StatusBanner dataConnector={dataConnector} />
    </div>
  );
}

function MetaItem({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      {icon}
      <span className="min-w-0 truncate text-sm leading-5 text-gray-600">
        {children}
      </span>
    </div>
  );
}

function StatusBanner({ dataConnector }: { dataConnector: DataConnector }) {
  if (dataConnector.status === "disabled") {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-5 text-sm leading-5 text-gray-700">
        This data connector is disabled. Enable it to resume{" "}
        {dataConnector.type === "api" ? "receiving data." : "pulling data."}
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-5 text-sm leading-5 text-gray-800">
      {dataConnector.type === "api" ? (
        <>
          Data connector is active and{" "}
          <span className="font-medium">
            ready to receive data via the Freshpaint API
          </span>
          .
        </>
      ) : (
        <>
          Data connector is active and{" "}
          <span className="font-medium">pulling data via Airbyte</span>.
        </>
      )}
    </div>
  );
}
